import React from 'react';
import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItemButton,
  ListItemText,
  Typography,
} from '@mui/material';
import { Armour } from '../../types';
import { useReferenceDataStore } from '../../stores/referenceDataStore';

interface SelectArmourModalProps {
  open: boolean;
  excludeArmourIds: string[];
  onClose: () => void;
  onSelect: (armour: Armour) => void;
}

export const SelectArmourModal: React.FC<SelectArmourModalProps> = ({
  open,
  excludeArmourIds,
  onClose,
  onSelect,
}) => {
  const allArmours = useReferenceDataStore((s) => s.armours);
  const available = React.useMemo(
    () =>
      [...allArmours]
        .filter((a) => !excludeArmourIds.includes(a.id))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [allArmours, excludeArmourIds]
  );

  const [selected, setSelected] = React.useState<Armour | null>(null);

  React.useEffect(() => {
    if (!open) setSelected(null);
  }, [open]);

  const handleConfirm = () => {
    if (selected) {
      onSelect(selected);
      setSelected(null);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add Armour</DialogTitle>
      <DialogContent dividers>
        {available.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No armour available to add.
          </Typography>
        ) : (
          <List disablePadding>
            {available.map((armour) => (
              <ListItemButton
                key={armour.id}
                selected={selected?.id === armour.id}
                onClick={() => setSelected(armour)}
                sx={{ borderRadius: 1, mb: 0.5 }}
              >
                <ListItemText
                  primary={
                    <Typography
                      variant="body1"
                      fontWeight={selected?.id === armour.id ? 600 : 400}
                    >
                      {armour.name}
                    </Typography>
                  }
                  secondary={
                    <>
                      {armour.armourModifier != null && (
                        <Chip
                          label={`Armour ${armour.armourModifier >= 0 ? '+' : ''}${armour.armourModifier}`}
                          size="small"
                          sx={{ mr: 1, mb: 0.5 }}
                        />
                      )}
                      {armour.description && (
                        <Typography variant="caption" color="text.secondary" component="span">
                          {armour.description}
                        </Typography>
                      )}
                    </>
                  }
                />
              </ListItemButton>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={!selected} onClick={handleConfirm}>
          Add Armour
        </Button>
      </DialogActions>
    </Dialog>
  );
};
